import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Button, Input, Spinner } from '@follow/ui';

import Service, { isServiceReady } from '../../services/Service';
import TeamService from '../../services/TeamService';
import { AcceptInvitationPayload } from '../../services/models/team';
import { REGEX_PASSWORD } from '../../services/models/user';
import { ApiError } from '../../types/common';

// Public page: the emailed token is the credential, so this route lives outside
// AuthGuard (and outside GuestGuard — an already-signed-in browser may still be
// redeeming an invitation sent to a different address).
const Invitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const token = searchParams.get('token');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!isServiceReady) {
      Service.init(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
    }
  }, [navigate]);

  const { data: invitation, isLoading, isError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => TeamService.getInvitation(token!),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (invitation) {
      setFirstName((current) => current || invitation.firstName || '');
      setLastName((current) => current || invitation.lastName || '');
    }
  }, [invitation]);

  const acceptMutation = useMutation({
    mutationFn: (payload: AcceptInvitationPayload) => TeamService.acceptInvitation(token!, payload),
    onSuccess: (response) => {
      // A brand-new user who just set a password gets a session; an existing
      // account is activated but must sign in itself.
      if (response.accessToken && response.refreshToken) {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.removeItem('locationId');
        queryClient.clear();
        navigate('/', { replace: true });
        return;
      }
      navigate('/login?invited=1', { replace: true });
    },
    onError: (error: ApiError) => {
      const messages: Record<string, string> = {
        INVALID_OR_EXPIRED_INVITE: 'Cette invitation est invalide ou a expiré.',
        PASSWORD_REQUIRED: 'Veuillez choisir un mot de passe.',
      };
      toast.error(messages[error.code ?? ''] ?? error.message ?? 'Une erreur est survenue.');
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (acceptMutation.isPending || !invitation) return;

    if (!invitation.requiresPassword) {
      acceptMutation.mutate({});
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Veuillez renseigner votre prénom et votre nom.');
      return;
    }
    if (!REGEX_PASSWORD.test(password)) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères, 1 majuscule, 1 minuscule et 1 chiffre.');
      return;
    }

    acceptMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.requestSubmit();
    }
  };

  const renderBody = () => {
    if (!token || isError) {
      return (
        <div className="w-full max-w-[448px]">
          <h1 className="text-white/80 typo-h1">Invitation invalide</h1>
          <p className="text-white/70 typo-body-lg mt-2">
            Ce lien d'invitation est invalide ou a expiré. Demandez à la personne qui vous a invité de vous en renvoyer un.
          </p>
          <p className="mt-8 text-white/70 typo-small">
            <Link to="/login" className="text-white/90 font-medium hover:underline">
              Retour à la connexion
            </Link>
          </p>
        </div>
      );
    }

    if (isLoading || !invitation) {
      return (
        <div className="w-full max-w-[448px] flex justify-center text-white/70">
          <Spinner size={24} />
        </div>
      );
    }

    return (
      <form className="w-full max-w-[448px]" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <h1 className="text-white/80 typo-h1">Rejoindre {invitation.locationName}</h1>
        <p className="text-white/70 typo-body-lg mt-2">
          {invitation.ownerName
            ? `${invitation.ownerName} vous invite à gérer ce site avec lui.`
            : 'Vous êtes invité à gérer ce site.'}
        </p>
        <p className="text-white/40 typo-small mt-2">{invitation.email}</p>

        {invitation.requiresPassword ? (
          <>
            <div className="mt-8 flex gap-3">
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="firstName">
                  Prénom
                </label>
                <Input
                  tone="light"
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="lastName">
                  Nom
                </label>
                <Input
                  tone="light"
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-white/70 typo-small mb-2" htmlFor="password">
                Mot de passe
              </label>
              <Input
                tone="light"
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <p className="mt-3 text-white/40 typo-small">
              8 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre
            </p>
          </>
        ) : (
          <p className="mt-8 text-white/70 typo-small">
            Vous avez déjà un compte Follow avec cette adresse. Acceptez l'invitation puis connectez-vous pour accéder à ce site.
          </p>
        )}

        <div className="mt-8">
          <Button
            type="submit"
            variant="primary"
            tone="light"
            size="lg"
            className="w-full"
            loading={acceptMutation.isPending}
            disabled={invitation.requiresPassword && (!firstName.trim() || !lastName.trim() || !password)}
          >
            {invitation.requiresPassword ? 'Créer mon compte' : 'Accepter l\'invitation'}
          </Button>
        </div>

        <p className="mt-6 text-center text-white/70 typo-small">
          <Link to="/login" className="text-white/90 font-medium hover:underline">
            Retour à la connexion
          </Link>
        </p>
      </form>
    );
  };

  return (
    <div className="flex min-h-screen bg-off-white overscroll-none overflow-hidden">
      {/* LEFT PANEL */}
      <div className="w-full lg:w-[35%] bg-secondary-900 flex flex-col">
        <div className="p-8">
          <a href={process.env.REACT_APP_LANDING_URL || '/'}>
            <img src="/logo_full_light.svg" alt="Follow" className="h-7 w-auto" />
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 lg:px-16">
          {renderBody()}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="hidden lg:flex lg:w-[65%] relative bg-secondary-900 border-l border-secondary-800 overflow-hidden lg:flex-col items-center justify-center">
        <img
          src="/signin-background.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/15" />

        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full gap-12">
          <h2 className="text-white typo-display text-center leading-tight">
            <span className="block">{'Transformez vos stations en'}</span>
            <span className="block text-primary-500">{' produits connectés'}</span>
          </h2>
          <p className="text-white typo-h3 text-center">
            Une solution intégrée pour piloter vos stations à distance.
          </p>

          <div className="flex justify-end -mt-32">
            <img
              src="/signin-lock.png"
              alt="Station Follow"
              className="w-[580px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invitation;
